/**
 * Zero Out Rules: Manage Capital's Segment 4. Per RAW investor code (not per pool, since
 * sql/45_): 4 fixed rules (DCB/FMV x Default/Delinquent) for when a delinquent or defaulted
 * contract stops carrying value.
 *
 * Targets athena_bfsi_prc.prc_zero_out_rules, re-keyed by
 * sql/45_prc_zero_out_rules_by_code.sql from one row per (pool, method, condition) to one row
 * per (raw BankDwn investor code, method, condition). investorCode/capitalType/facilityCode
 * are now display-only — a code maps to a pool through prc_facility_investor_code_map, and a
 * code mapped to none or several pools shows those fields null (facilityLabel falls back to
 * "Code {bankdwnInvestorCode}").
 *
 * Every code always carries exactly these 4 rules — prc_sync_zero_out_rules() seeds them for
 * any LIVE code that lacks them and never touches an existing one, so there is no create path
 * any more (Bankruptcy and the FLAG shape it used were both removed by sql/45_ too — nothing
 * ever used them). Only PATCH exists here.
 *
 * Direct write, no propose/approve, same reasoning as eligibility.ts. sql/34_'s original "an
 * FMV rule can't be ON for a pool that already prices FMV off the grid" invariant is GONE per
 * sql/45_'s header — the business chose to allow it — so this module no longer checks
 * discountSettings.ts's fmvBasis at all. See
 * portfolio-risk-concentrations/app/router/zero_out_rules.py's module docstring.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getJson, patchJson, PRC_PREFIX } from "./client";

export type ZeroOutRuleShape = "THRESHOLD";
export type ZeroOutCondition = "Delinquent" | "Default";

export const ZERO_OUT_CONDITIONS: ZeroOutCondition[] = ["Delinquent", "Default"];

export interface ZeroOutRule {
  ruleId: string;
  /** The raw BankDwn investor code (e.g. "10", "40"), not a prc_investor pool name. */
  bankdwnInvestorCode: string;
  investorCode: string | null;
  capitalType: "Debt" | "Equity" | null;
  facilityCode: string | null;
  facilityLabel: string;
  method: "DCB" | "FMV";
  condition: ZeroOutCondition;
  /** Always THRESHOLD since sql/45_ — kept for display, nothing branches on it any more. */
  ruleShape: ZeroOutRuleShape;
  dayCount: number | null;
  /** A percent, 10 means 10%, never a fraction. */
  pctOfPayments: number | null;
  status: "ON" | "OFF";
  offReason: string | null;
  updatedAt: string | null;
}

/** The columns the table can be sorted by (the backend's own list). A blank Day Count / % of Payments always sorts last. */
export type ZeroOutSortKey =
  | "investorCode"
  | "facility"
  | "method"
  | "condition"
  | "dayCount"
  | "pctOfPayments"
  | "status";

/** One page of rules, sorted by `sort` across ALL rows (not just this page) when it is set; the table's own order otherwise. */
export function useZeroOutRules(page: {
  limit: number;
  offset: number;
  sort?: ZeroOutSortKey | null;
  dir?: "asc" | "desc";
}) {
  return useQuery({
    queryKey: ["prc-zero-out-rules", page],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      if (page.sort) {
        q.set("sort", page.sort);
        q.set("dir", page.dir ?? "asc");
      }
      return getJson<{ rules: ZeroOutRule[]; total: number }>(
        `${PRC_PREFIX}zero-out-rules?${q.toString()}`,
      );
    },
  });
}

export function useUpdateZeroOutRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      ruleId: string;
      dayCount?: number;
      pctOfPayments?: number;
      status?: "ON" | "OFF";
      offReason?: string | null;
    }) =>
      patchJson<{ ok: boolean }>(
        `${PRC_PREFIX}zero-out-rules/${encodeURIComponent(input.ruleId)}`,
        {
          dayCount: input.dayCount,
          pctOfPayments: input.pctOfPayments,
          status: input.status,
          offReason: input.offReason,
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prc-zero-out-rules"] }),
  });
}
